import type { Hazard, ItemBox, ItemId, Projectile, Tumbleweed } from '../core/types';
import { ITEM_BY_ID } from '../data/items';
import { clamp, TAU } from '../core/math';

/**
 * Item-világ renderelés. A hívó már beállította a kamerát (világkoordináták),
 * kivéve a drawItemIcon-t, ami képernyő-koordinátában dolgozik (HUD).
 * Minden rajz olcsó: nincs shadowBlur, néhány path / tárgy.
 */

/** Sivatagi ördögszekér: száraz gallyakból álló, guruló gömb. */
export function drawTumbleweeds(ctx: CanvasRenderingContext2D, weeds: Tumbleweed[]): void {
  for (const weed of weeds) {
    const r = weed.radius;
    ctx.save();
    ctx.translate(weed.pos.x, weed.pos.y);
    ctx.fillStyle = 'rgba(60,40,20,0.26)';
    ctx.beginPath();
    ctx.ellipse(5, 7, r * 0.95, r * 0.5, 0, 0, TAU);
    ctx.fill();
    ctx.rotate(weed.rotation);
    ctx.strokeStyle = 'rgba(132,87,42,0.95)';
    ctx.lineWidth = Math.max(1.6, r * 0.12);
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.72, 0, TAU);
    ctx.stroke();
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 + 0.35;
      ctx.beginPath();
      ctx.moveTo(Math.cos(a) * r * 0.2, Math.sin(a) * r * 0.2);
      ctx.lineTo(Math.cos(a + 0.7) * r, Math.sin(a + 0.7) * r);
      ctx.stroke();
    }
    ctx.strokeStyle = 'rgba(190,140,80,0.5)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(0, 0, r * 0.42, 0, TAU);
    ctx.stroke();
    ctx.restore();
  }
}

const BOX_RESPAWN = 4;

const BOX_TOP = '#ffe6a0';
const BOX_LEFT = '#f2a63b';
const BOX_RIGHT = '#d9822b';

/** Lekerekített téglalap path (roundRect nélkül, régi böngészők miatt). */
const roundRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void => {
  const rr = Math.min(r, Math.min(w, h) * 0.5);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
};

/** Négyszirmú csillanás. */
const sparkle = (ctx: CanvasRenderingContext2D, x: number, y: number, r: number, rot: number): void => {
  ctx.beginPath();
  for (let i = 0; i < 8; i++) {
    const a = rot + (i / 8) * TAU;
    const rad = i % 2 === 0 ? r : r * 0.34;
    const px = x + Math.cos(a) * rad;
    const py = y + Math.sin(a) * rad;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fill();
};

/** Lebegő „csomagautomata" kocka, forgó csillanással; az inaktív halvány. */
export function drawItemBoxes(ctx: CanvasRenderingContext2D, boxes: ItemBox[], time: number): void {
  for (const box of boxes) {
    const x = box.pos.x;
    const y = box.pos.y;
    const bob = Math.sin(time * 2.3 + box.phase) * 2.4;

    // talajárnyék
    ctx.fillStyle = 'rgba(10, 14, 22, 0.24)';
    ctx.beginPath();
    ctx.ellipse(x, y + 8, 19, 7.5, 0, 0, TAU);
    ctx.fill();

    if (!box.active) {
      // visszatöltődő doboz: halvány váz + töltőív
      const p = 1 - clamp(box.respawn / BOX_RESPAWN, 0, 1);
      ctx.globalAlpha = 0.3;
      ctx.strokeStyle = '#b9c7d6';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(x, y - 2, 15, 0, TAU);
      ctx.stroke();
      if (p > 0.02) {
        ctx.globalAlpha = 0.85;
        ctx.strokeStyle = BOX_TOP;
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.arc(x, y - 2, 15, -Math.PI / 2, -Math.PI / 2 + TAU * p);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
      continue;
    }

    const lift = y - 16 + bob;
    const hw = 19; // fél szélesség
    const fh = 8.5; // felső rombusz félmagassága
    const dh = 15; // kocka magassága

    // felső lap
    ctx.fillStyle = BOX_TOP;
    ctx.beginPath();
    ctx.moveTo(x, lift - fh);
    ctx.lineTo(x + hw, lift);
    ctx.lineTo(x, lift + fh);
    ctx.lineTo(x - hw, lift);
    ctx.closePath();
    ctx.fill();

    // bal első oldallap
    ctx.fillStyle = BOX_LEFT;
    ctx.beginPath();
    ctx.moveTo(x - hw, lift);
    ctx.lineTo(x, lift + fh);
    ctx.lineTo(x, lift + fh + dh);
    ctx.lineTo(x - hw, lift + dh);
    ctx.closePath();
    ctx.fill();

    // jobb oldallap
    ctx.fillStyle = BOX_RIGHT;
    ctx.beginPath();
    ctx.moveTo(x + hw, lift);
    ctx.lineTo(x, lift + fh);
    ctx.lineTo(x, lift + fh + dh);
    ctx.lineTo(x + hw, lift + dh);
    ctx.closePath();
    ctx.fill();

    // élek
    ctx.strokeStyle = 'rgba(90, 52, 12, 0.55)';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(x - hw, lift);
    ctx.lineTo(x, lift + fh);
    ctx.lineTo(x + hw, lift);
    ctx.moveTo(x, lift + fh);
    ctx.lineTo(x, lift + fh + dh);
    ctx.stroke();

    // ragasztószalag a felső lapon
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(x - hw * 0.62, lift - fh * 0.38);
    ctx.lineTo(x + hw * 0.62, lift + fh * 0.38);
    ctx.stroke();

    // forgó csillanás
    ctx.fillStyle = '#fffbe8';
    sparkle(ctx, x, lift, 6.5, time * 1.7 + box.phase);
  }
}

const paintOil = (ctx: CanvasRenderingContext2D, h: Hazard, time: number): void => {
  const r = h.radius;
  const x = h.pos.x;
  const y = h.pos.y;

  ctx.fillStyle = ITEM_BY_ID.oil.color;
  ctx.beginPath();
  ctx.ellipse(x, y, r, r * 0.74, 0, 0, TAU);
  ctx.fill();

  // kékesszürke csillanás
  ctx.fillStyle = 'rgba(126, 148, 182, 0.35)';
  ctx.beginPath();
  ctx.ellipse(x - r * 0.18, y - r * 0.18, r * 0.44, r * 0.2, -0.35, 0, TAU);
  ctx.fill();

  // mozgó fénypont
  const sx = x + Math.sin(time * 1.5 + h.id) * r * 0.4;
  ctx.fillStyle = 'rgba(198, 224, 255, 0.28)';
  ctx.beginPath();
  ctx.ellipse(sx, y + r * 0.22, r * 0.2, r * 0.09, 0.5, 0, TAU);
  ctx.fill();

  // olajos perem: hűvös fénygyűrű, hogy a sötét folt az aszfalton is olvasható legyen
  ctx.strokeStyle = 'rgba(122, 150, 186, 0.35)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.ellipse(x, y, r, r * 0.74, 0, 0, TAU);
  ctx.stroke();
};

const paintGlue = (ctx: CanvasRenderingContext2D, h: Hazard, time: number): void => {
  const r = h.radius;
  const x = h.pos.x;
  const y = h.pos.y;
  const n = 9;

  // szabálytalan paca
  ctx.beginPath();
  for (let i = 0; i < n; i++) {
    const a = (i / n) * TAU;
    const wob = 1 + Math.sin(a * 3 + time * 1.2 + h.id) * 0.06;
    const px = x + Math.cos(a) * r * wob;
    const py = y + Math.sin(a) * r * 0.78 * wob;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fillStyle = ITEM_BY_ID.glue.color;
  ctx.fill();
  ctx.strokeStyle = '#b6611f';
  ctx.lineWidth = 2.4;
  ctx.stroke();

  // ragacs-fény
  ctx.fillStyle = 'rgba(255, 226, 168, 0.55)';
  ctx.beginPath();
  ctx.ellipse(x - r * 0.22, y - r * 0.2, r * 0.3, r * 0.14, -0.4, 0, TAU);
  ctx.fill();

  // folyó cseppek
  ctx.fillStyle = '#d97a2c';
  for (let i = 0; i < 3; i++) {
    const a = time * 0.5 + i * 2.1 + h.id;
    ctx.beginPath();
    ctx.arc(x + Math.cos(a) * r * 0.85, y + Math.sin(a) * r * 0.62, r * 0.12, 0, TAU);
    ctx.fill();
  }
};

/** Olajfolt sötét, fényes pacaként; ragacs narancs paca. A lejárat előtt elhalványul. */
export function drawHazards(ctx: CanvasRenderingContext2D, hazards: Hazard[], time: number): void {
  for (const h of hazards) {
    const fade = h.life < 2 ? clamp(h.life / 2, 0, 1) : 1;
    if (fade <= 0) continue;
    ctx.globalAlpha = fade;

    // enyhe talajárnyék
    ctx.fillStyle = 'rgba(10, 14, 20, 0.18)';
    ctx.beginPath();
    ctx.ellipse(h.pos.x + 2, h.pos.y + 3, h.radius * 1.02, h.radius * 0.74, 0, 0, TAU);
    ctx.fill();

    if (h.kind === 'glue') paintGlue(ctx, h, time);
    else paintOil(ctx, h, time);

    ctx.globalAlpha = 1;
  }
}

/** Szódásüveg lövedék: orr irányába forgatva, kis füstcsíkkal. */
export function drawProjectiles(ctx: CanvasRenderingContext2D, projectiles: Projectile[], time: number): void {
  for (const p of projectiles) {
    const ang = Math.atan2(p.vel.y, p.vel.x);
    const ca = Math.cos(ang);
    const sa = Math.sin(ang);
    const r = p.radius;

    // szódás füstcsík
    for (let i = 1; i <= 3; i++) {
      const back = r + i * 7 + Math.sin(time * 12 + i * 2 + p.id) * 1.5;
      ctx.globalAlpha = 0.3 - i * 0.07;
      ctx.fillStyle = '#e6f7ff';
      ctx.beginPath();
      ctx.arc(p.pos.x - ca * back, p.pos.y - sa * back, r * (0.46 - i * 0.09), 0, TAU);
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    ctx.save();
    ctx.translate(p.pos.x, p.pos.y);
    ctx.rotate(ang);

    // üveg test
    ctx.fillStyle = ITEM_BY_ID.soda.color;
    roundRect(ctx, -r * 0.72, -r * 0.46, r * 1.08, r * 0.92, r * 0.24);
    ctx.fill();

    // címke
    ctx.fillStyle = 'rgba(20, 40, 16, 0.35)';
    ctx.fillRect(-r * 0.26, -r * 0.46, r * 0.34, r * 0.92);

    // üveg fény
    ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
    roundRect(ctx, -r * 0.54, -r * 0.28, r * 0.16, r * 0.56, r * 0.08);
    ctx.fill();

    // nyak + kupak
    ctx.fillStyle = ITEM_BY_ID.soda.color;
    ctx.fillRect(r * 0.32, -r * 0.24, r * 0.34, r * 0.48);
    ctx.fillStyle = '#f4ffe8';
    ctx.fillRect(r * 0.56, -r * 0.3, r * 0.16, r * 0.6);

    ctx.restore();
  }
}

/** A HUD ikonjai – képernyő-koordinátában, normált -1..1 térben rajzolva. */
const INK: Partial<Record<ItemId, string>> = {
  oil: '#9aa7b8',
  glue: '#f9a45c',
};

const drawGlyph = (ctx: CanvasRenderingContext2D, id: ItemId): void => {
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  switch (id) {
    case 'turbo': {
      ctx.lineWidth = 0.3;
      for (const ox of [-0.34, 0.18]) {
        ctx.beginPath();
        ctx.moveTo(ox - 0.16, -0.62);
        ctx.lineTo(ox + 0.3, 0);
        ctx.lineTo(ox - 0.16, 0.62);
        ctx.stroke();
      }
      break;
    }

    case 'oil': {
      ctx.beginPath();
      ctx.ellipse(0, 0.1, 0.72, 0.5, 0, 0, TAU);
      ctx.fill();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
      ctx.beginPath();
      ctx.ellipse(0.18, -0.1, 0.2, 0.1, -0.4, 0, TAU);
      ctx.fill();
      break;
    }

    case 'soda': {
      ctx.save();
      ctx.rotate(-0.55);
      roundRect(ctx, -0.34, -0.5, 0.68, 1.0, 0.22);
      ctx.fill();
      ctx.fillRect(-0.14, -0.78, 0.28, 0.3);
      ctx.fillStyle = 'rgba(240, 255, 225, 0.85)';
      ctx.fillRect(-0.2, -0.9, 0.4, 0.16);
      ctx.fillStyle = 'rgba(14, 20, 30, 0.45)';
      ctx.fillRect(-0.34, -0.12, 0.68, 0.22);
      ctx.restore();
      break;
    }

    case 'lightning': {
      ctx.beginPath();
      ctx.moveTo(0.2, -0.9);
      ctx.lineTo(-0.55, 0.12);
      ctx.lineTo(-0.06, 0.12);
      ctx.lineTo(-0.28, 0.9);
      ctx.lineTo(0.58, -0.2);
      ctx.lineTo(0.08, -0.2);
      ctx.closePath();
      ctx.fill();
      break;
    }

    case 'shield': {
      // bevásárlószatyor
      ctx.beginPath();
      ctx.moveTo(-0.62, -0.18);
      ctx.lineTo(0.62, -0.18);
      ctx.lineTo(0.48, 0.82);
      ctx.lineTo(-0.48, 0.82);
      ctx.closePath();
      ctx.fill();
      ctx.lineWidth = 0.16;
      ctx.beginPath();
      ctx.arc(0, -0.2, 0.3, Math.PI, TAU);
      ctx.stroke();
      break;
    }

    case 'glue': {
      ctx.beginPath();
      ctx.moveTo(0, -0.78);
      ctx.lineTo(0.78, 0.62);
      ctx.lineTo(-0.78, 0.62);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      ctx.arc(0.02, 0.86, 0.16, 0, TAU);
      ctx.fill();
      break;
    }
  }
};

export function drawItemIcon(ctx: CanvasRenderingContext2D, id: ItemId, cx: number, cy: number, size: number): void {
  if (!(size > 0)) return;
  const def = ITEM_BY_ID[id];
  const half = size * 0.5;

  ctx.save();
  ctx.translate(cx, cy);

  // sötét pajzs a színnel körberajzolva
  roundRect(ctx, -half, -half, size, size, size * 0.28);
  ctx.fillStyle = 'rgba(14, 20, 30, 0.86)';
  ctx.fill();
  ctx.lineWidth = Math.max(1, size * 0.055);
  ctx.strokeStyle = def.color;
  ctx.stroke();

  // ikon -1..1 normált térben
  ctx.save();
  ctx.scale(half * 0.76, half * 0.76);
  ctx.fillStyle = INK[id] ?? def.color;
  ctx.strokeStyle = ctx.fillStyle;
  drawGlyph(ctx, id);
  ctx.restore();

  ctx.restore();
}
